import {
  AiChFeatureStatus,
  type AiChSettingsDto,
  AiChattingService,
  FeatureFlagName,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes'
import { $creatorId } from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags'

import { initializeApp } from '../init'
import { $currentPage } from '../router'

export const loadAiChSettingsFx = attach({
  source: { creatorId: $creatorId },
  effect: createEffect(async ({ creatorId }: { creatorId: number | null }) => {
    if (!creatorId) return null

    return AiChattingService.getAiChSettings({ creatorId })
  }),
})

export const updateAiChSettingsFx = attach({
  source: { creatorId: $creatorId },
  mapParams: (
    requestBody: { mode: boolean; state: boolean },
    { creatorId },
  ) => ({
    creatorId: creatorId!,
    requestBody,
  }),
  effect: createEffect(AiChattingService.updateAiChSettings),
})

export const setCustomAiChSettingsValue = createEvent<{
  key: keyof AiChSettingsDto
  value: unknown
}>('setCustomAiChSettingsValue')

export const $aiChSettings = createStore<AiChSettingsDto | null>(null)
  .on(loadAiChSettingsFx.doneData, (_, settings) => settings)
  .on(updateAiChSettingsFx.doneData, (_, settings) => settings)
  .on(setCustomAiChSettingsValue, (state, { key, value }) => {
    if (!state) return state

    return {
      ...state,
      [key]: value,
    }
  })

export const $isAiChattingEnabled = combine(
  { settings: $aiChSettings, availableFeatures: $availableFeatures },
  ({ settings, availableFeatures }) => {
    const isFeatureStatusEnabled =
      settings?.featureStatus === AiChFeatureStatus.ENABLED
    const isFeatureFlagAvailable = isFeatureAvailableUtil(
      availableFeatures,
      FeatureFlagName.AI_CHATTING_FEATURE,
    )

    return (
      isFeatureStatusEnabled && isFeatureFlagAvailable && !!availableFeatures
    )
  },
)

export const $isAiChattingInitializing = $aiChSettings.map(
  (settings) => settings?.featureStatus === AiChFeatureStatus.INITIALIZING,
)

export const changeAiChSetting = createEvent<{
  mode: boolean
  state: boolean
}>('changeAiChSetting')

sample({
  clock: changeAiChSetting,
  target: updateAiChSettingsFx,
})

export const setAiChCustomValue = (
  key: keyof AiChSettingsDto,
  value: unknown,
) => {
  setCustomAiChSettingsValue({ key, value })
}

sample({
  clock: [initializeApp, $currentPage, $availableFeatures],
  source: { path: $currentPage, availableFeatures: $availableFeatures },
  filter: ({ path, availableFeatures }) => {
    return (
      (!!matchPath(Pages.CHATTING_UI, path) ||
        !!matchPath(Pages.CREATOR_AICH_ALPHA, path) ||
        !!matchPath(Pages.CREATOR_VAULT, path)) &&
      isFeatureAvailableUtil(
        availableFeatures,
        FeatureFlagName.AI_CHATTING_FEATURE,
        false,
      )
    )
  },
  target: loadAiChSettingsFx,
})
